import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Avatar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Rating as MuiRating,
  Menu,
  MenuItem as MuiMenuItem,
  useTheme,
  Link,
  Tabs,
  Tab,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ArrowBack,
  Business as BusinessIcon,
  LocationOn,
  Phone,
  Email,
  Language,
  CheckCircle,
  Cancel,
  Info,
  HelpOutline,
  Block,
  PlayArrow,
  WhatsApp,
  PictureAsPdf,
  Verified,
  Instagram,
  Facebook,
  YouTube,
  Twitter,
  Schedule,
  MoreVert,
  AttachFile,
  Badge,
  CalendarMonth,
  People,
  Map,
  Apartment,
  Inventory,
  MiscellaneousServices,
  Star,
  ShareOutlined,
  School,
  AccessTime,
  WorkspacePremium,
} from '@mui/icons-material';
import { Business, ServiceProduct, ServiceType, BusinessCourse, CourseLevel, CourseType as CourseTypeEnum } from '@/constants/types';
import { businessService } from '@/services/business/businessService';
import { serviceProductService } from '@/services/business/serviceProductService';
import { businessCourseService } from '@/services/business/businessCourseService';
import { slateJsonToText } from '@/utils/slateSerializer';
import { ConfirmDialog } from '@/components/common';
import toast from 'react-hot-toast';
import { getFileUrl, getFileName } from '@/utils/businessHelpers';
import { useNavigate, useParams } from 'react-router-dom';
import { CanvasPdfViewer } from '@/components/dashboard/feed/CanvasPdfViewer';
import { ShareProductModal } from '@/components/business/ShareProductModal';
import { ShareCourseModal } from '@/components/business/ShareCourseModal';
import moment from 'moment';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Helper function to convert 24-hour time to 12-hour format
const formatTime = (time: string): string => {
  if (!time) return '';
  return moment(time, 'HH:mm').format('h:mm A');
};

export const BusinessDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();

  const [business, setBusiness] = useState<Business | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  // Products/Services state
  const [serviceProducts, setServiceProducts] = useState<ServiceProduct[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [serviceTypeFilter, setServiceTypeFilter] = useState<ServiceType | 'all'>('all');
  const [shareProduct, setShareProduct] = useState<ServiceProduct | null>(null);

  // Courses state
  const [courses, setCourses] = useState<BusinessCourse[]>([]);
  const [isLoadingCourses, setIsLoadingCourses] = useState(false);
  const [courseLevelFilter, setCourseLevelFilter] = useState<CourseLevel | 'all'>('all');
  const [courseTypeFilter, setCourseTypeFilter] = useState<CourseTypeEnum | 'all'>('all');
  const [shareCourse, setShareCourse] = useState<BusinessCourse | null>(null);

  // Dialog states
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [clarificationDialogOpen, setClarificationDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{ action: string; title: string; message: string } | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [clarificationNote, setClarificationNote] = useState('');

  // PDF Preview dialog state
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [pdfFileName, setPdfFileName] = useState<string>('');

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (id) {
      loadBusiness();
    }
  }, [id]);

  // Load products when business is verified
  useEffect(() => {
    if (business?.isVerified && id) {
      loadServiceProducts();
    }
  }, [business?.isVerified, id, serviceTypeFilter]);

  // Load courses when business is verified
  useEffect(() => {
    if (business?.isVerified && id) {
      loadBusinessCourses();
    }
  }, [business?.isVerified, id, courseLevelFilter, courseTypeFilter]);

  const loadServiceProducts = async () => {
    if (!id) return;
    try {
      setIsLoadingProducts(true);
      const response = await serviceProductService.getServiceProducts({
        businessId: id,
        limit: 50,
        serviceType: serviceTypeFilter !== 'all' ? serviceTypeFilter : undefined,
      });
      setServiceProducts(response.data.serviceProducts);
    } catch (err: any) {
      console.error('Failed to load products:', err);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const loadBusinessCourses = async () => {
    if (!id) return;
    try {
      setIsLoadingCourses(true);
      const response = await businessCourseService.getBusinessCourses({
        businessId: id,
        limit: 50,
        level: courseLevelFilter !== 'all' ? courseLevelFilter : undefined,
        courseType: courseTypeFilter !== 'all' ? courseTypeFilter : undefined,
      });
      setCourses(response.data.businessCourses);
    } catch (err: any) {
      console.error('Failed to load courses:', err);
    } finally {
      setIsLoadingCourses(false);
    }
  };

  const loadBusiness = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      setError(null);
      const response = await businessService.getBusiness(id);
      setBusiness(response.data.business);
    } catch (err: any) {
      setError(err.message || 'Failed to load business details');
      toast.error(err.message || 'Failed to load business details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenPdfPreview = (url: string, fileName: string) => {
    setPdfUrl(url);
    setPdfFileName(fileName);
    setPdfPreviewOpen(true);
  };

  const handleClosePdfPreview = () => {
    setPdfPreviewOpen(false);
    setPdfUrl('');
    setPdfFileName('');
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuAction = (action: () => void) => {
    action();
    handleMenuClose();
  };

  const handleApprove = () => {
    setConfirmAction({
      action: 'approve',
      title: 'Approve Business',
      message: `Are you sure you want to approve "${business?.businessName}"?`,
    });
    setConfirmDialogOpen(true);
  };

  const handleReject = () => {
    setRejectDialogOpen(true);
  };

  const handleSetInReview = () => {
    setConfirmAction({
      action: 'in_review',
      title: 'Set In Review',
      message: `Are you sure you want to set "${business?.businessName}" status to in review?`,
    });
    setConfirmDialogOpen(true);
  };

  const handleRequestClarification = () => {
    setClarificationDialogOpen(true);
  };

  const handleSuspend = () => {
    setConfirmAction({
      action: 'suspend',
      title: 'Suspend Business',
      message: `Are you sure you want to suspend "${business?.businessName}"?`,
    });
    setConfirmDialogOpen(true);
  };

  const handleActivate = () => {
    setConfirmAction({
      action: 'activate',
      title: 'Activate Business',
      message: `Are you sure you want to activate "${business?.businessName}"?`,
    });
    setConfirmDialogOpen(true);
  };

  const handleBlock = () => {
    setConfirmAction({
      action: 'block',
      title: 'Block Business',
      message: `Are you sure you want to block "${business?.businessName}"? This will prevent the business from being visible.`,
    });
    setConfirmDialogOpen(true);
  };

  const handleUnblock = () => {
    setConfirmAction({
      action: 'unblock',
      title: 'Unblock Business',
      message: `Are you sure you want to unblock "${business?.businessName}"?`,
    });
    setConfirmDialogOpen(true);
  };

  const executeConfirmAction = async () => {
    if (!business || !confirmAction) return;

    try {
      switch (confirmAction.action) {
        case 'approve':
          await businessService.approveBusiness(business._id);
          toast.success('Business approved successfully');
          break;
        case 'in_review':
          await businessService.setInReview(business._id);
          toast.success('Business status updated to in review');
          break;
        case 'suspend':
          await businessService.suspendBusiness(business._id);
          toast.success('Business suspended successfully');
          break;
        case 'activate':
          await businessService.activateBusiness(business._id);
          toast.success('Business activated successfully');
          break;
        case 'block':
          await businessService.blockBusiness(business._id);
          toast.success('Business blocked successfully');
          break;
        case 'unblock':
          await businessService.unblockBusiness(business._id);
          toast.success('Business unblocked successfully');
          break;
      }
      setConfirmDialogOpen(false);
      setConfirmAction(null);
      loadBusiness();
    } catch (err: any) {
      toast.error(err.message || 'Operation failed');
    }
  };

  const handleRejectSubmit = async () => {
    if (!business || !rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      await businessService.rejectBusiness(business._id, rejectReason);
      toast.success('Business rejected successfully');
      setRejectDialogOpen(false);
      setRejectReason('');
      loadBusiness();
    } catch (err: any) {
      toast.error(err.message || 'Failed to reject business');
    }
  };

  const handleClarificationSubmit = async () => {
    if (!business || !clarificationNote.trim()) {
      toast.error('Please provide a clarification note');
      return;
    }

    try {
      await businessService.requestClarification(business._id, clarificationNote);
      toast.success('Clarification requested successfully');
      setClarificationDialogOpen(false);
      setClarificationNote('');
      loadBusiness();
    } catch (err: any) {
      toast.error(err.message || 'Failed to request clarification');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'in_review':
        return 'info';
      case 'clarification_needed':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !business) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Business not found'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/manage-business')} sx={{ mt: 2 }}>
          Back to Manage Businesses
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin/manage-business')}
          sx={{ textTransform: 'none' }}
        >
          Back
        </Button>
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={handleMenuOpen} title="Admin Actions">
          <MoreVert />
        </IconButton>
      </Box>

      {/* Top: Banner with Logo */}
      <Box sx={{ position: 'relative', mb: 10 }}>
        {/* Banner */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: { xs: 180, sm: 240, md: 300 },
            overflow: 'hidden',
            bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
          }}
        >
          {getFileUrl(business.bannerId) ? (
            <Box
              component="img"
              src={getFileUrl(business.bannerId)}
              alt={`${business.businessName} Banner`}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}22 0%, ${theme.palette.primary.main}44 100%)`,
              }}
            >
              <BusinessIcon sx={{ fontSize: { xs: 60, md: 100 }, color: 'text.disabled' }} />
            </Box>
          )}
        </Box>

        {/* Logo - Overlapping */}
        <Box
          sx={{
            position: 'absolute',
            bottom: -50,
            left: { xs: 16, sm: 24 },
          }}
        >
          <Avatar
            src={getFileUrl(business.logoId)}
            alt={business.businessName}
            sx={{
              width: { xs: 100, sm: 120, md: 140 },
              height: { xs: 100, sm: 120, md: 140 },
              border: `4px solid ${theme.palette.background.paper}`,
              bgcolor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.300',
              boxShadow: 3,
              fontSize: { xs: 36, sm: 42, md: 48 },
            }}
          >
            {!getFileUrl(business.logoId) && business.businessName?.charAt(0).toUpperCase()}
          </Avatar>
        </Box>
      </Box>

      {/* Badges */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {Array.isArray(business.businessCategory) ? (
          business.businessCategory.map((category) => (
            <Chip key={category} label={category} size="small" color="primary" variant="outlined" />
          ))
        ) : (
          <Chip label={business.businessCategory} size="small" color="primary" variant="outlined" />
        )}
        <Chip
          label={getStatusLabel(business.approvalStatus)}
          color={getStatusColor(business.approvalStatus)}
          size="small"
        />
        {business.isVerified && (
          <Chip
            icon={<Verified sx={{ fontSize: 14 }} />}
            label="Verified"
            size="small"
            color="success"
            variant="outlined"
          />
        )}
        {business.isBlocked && <Chip label="Blocked" size="small" color="error" />}
        {business.isSuspended && <Chip label="Suspended" size="small" color="warning" />}
        {business.isActive && <Chip label="Active" size="small" color="success" variant="outlined" />}
      </Box>

      {/* Title */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              letterSpacing: -1,
              lineHeight: 1.05,
              fontSize: { xs: '1.75rem', sm: '2.25rem' },
            }}
          >
            {business.businessName}
          </Typography>
          {business.isVerified && (
            <Verified sx={{ fontSize: 28, color: 'primary.main' }} />
          )}
        </Box>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: 1.5,
            py: 0.4,
            borderRadius: 5,
            bgcolor: 'action.hover',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: 0.2 }}
          >
            {business.businessUsername}
          </Typography>
        </Box>
      </Box>

      {/* Rating */}
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1.25,
          mb: 3,
          px: 2,
          py: 1,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
        }}
      >
        <MuiRating value={business.rating || 0} precision={0.1} readOnly size="small" />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {business.rating?.toFixed(1) || '0.0'}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
          ({business.ratingsCount || 0} {business.ratingsCount === 1 ? 'rating' : 'ratings'})
        </Typography>
      </Box>

      {/* Tabs - Only show Products tab for verified businesses */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Business Info" sx={{ textTransform: 'none', fontWeight: 600 }} />
          {business.isVerified && (
            <Tab
              label={`Products & Services${serviceProducts.length > 0 ? ` (${serviceProducts.length})` : ''}`}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
          )}
          {business.isVerified && (
            <Tab
              label={`Courses${courses.length > 0 ? ` (${courses.length})` : ''}`}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
          )}
        </Tabs>
      </Box>

      {/* Business Info Tab */}
      {activeTab === 0 && (
        <>
          {/* Description */}
      {business.description && (
        <>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2.5, pl: 1.5, borderLeft: '4px solid', borderColor: 'primary.main', lineHeight: 1.2 }}>
            About
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              p: 2.5,
              mb: 3,
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: theme.shadows[3],
              },
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  lineHeight: 1.85,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {business.description}
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ mb: 3 }} />
        </>
      )}

      {/* Business Information */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2.5, pl: 1.5, borderLeft: '4px solid', borderColor: 'primary.main', lineHeight: 1.2 }}>
        Business Information
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Address */}
        <Grid size={{ xs: 12 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              p: 2.5,
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: theme.shadows[3],
              },
            }}
          >
            <LocationOn sx={{ fontSize: 22, color: 'primary.main', mt: 0.2 }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>
                Address
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                {business.addressLine1}
                {business.addressLine2 && `, ${business.addressLine2}`}
                <br />
                {business.landmark && `${business.landmark}, `}
                {business.city}, {business.state} - {business.pincode}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Owner */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              p: 2.5,
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%',
              transition: 'border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: theme.shadows[3],
                transform: 'translateY(-2px)',
              },
            }}
          >
            <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
              {business.ownerName.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.68rem', fontWeight: 600, mb: 0.4 }}>
                Owner
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {business.ownerName}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Contact Number */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              p: 2.5,
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%',
              transition: 'border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: theme.shadows[3],
                transform: 'translateY(-2px)',
              },
            }}
          >
            <Phone sx={{ fontSize: 20, color: 'primary.main', mt: 0.5 }} />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.68rem', fontWeight: 600, mb: 0.4 }}>
                Contact Number
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {business.contactNumber}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* WhatsApp */}
        {business.whatsappNumber && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              p: 2.5,
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%',
              transition: 'border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: theme.shadows[3],
                transform: 'translateY(-2px)',
              },
            }}
          >
              <WhatsApp sx={{ fontSize: 20, color: 'success.main', mt: 0.5 }} />
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.68rem', fontWeight: 600, mb: 0.4 }}>
                  WhatsApp
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {business.whatsappNumber}
                </Typography>
              </Box>
            </Box>
          </Grid>
        )}

        {/* Landline */}
        {business.landlineNumber && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              p: 2.5,
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%',
              transition: 'border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: theme.shadows[3],
                transform: 'translateY(-2px)',
              },
            }}
          >
              <Phone sx={{ fontSize: 20, color: 'primary.main', mt: 0.5 }} />
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.68rem', fontWeight: 600, mb: 0.4 }}>
                  Landline
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {business.landlineNumber}
                </Typography>
              </Box>
            </Box>
          </Grid>
        )}

        {/* Email */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              p: 2.5,
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%',
              transition: 'border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: theme.shadows[3],
                transform: 'translateY(-2px)',
              },
            }}
          >
            <Email sx={{ fontSize: 20, color: 'primary.main', mt: 0.5 }} />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.68rem', fontWeight: 600, mb: 0.4 }}>
                Email
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {business.email}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Website */}
        {business.website && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              p: 2.5,
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%',
              transition: 'border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: theme.shadows[3],
                transform: 'translateY(-2px)',
              },
            }}
          >
              <Language sx={{ fontSize: 20, color: 'primary.main', mt: 0.5 }} />
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.68rem', fontWeight: 600, mb: 0.4 }}>
                  Website
                </Typography>
                <Link href={business.website} target="_blank" rel="noopener noreferrer" variant="body1" fontWeight={500}>
                  {business.website}
                </Link>
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Services Offered */}
      {business.businessServices && (Array.isArray(business.businessServices) ? business.businessServices.length > 0 : !!business.businessServices) && (
        <>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2.5, pl: 1.5, borderLeft: '4px solid', borderColor: 'primary.main', lineHeight: 1.2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <MiscellaneousServices /> Services Offered
          </Typography>
          <Box
            sx={{
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              mb: 3,
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {(Array.isArray(business.businessServices) ? business.businessServices : [business.businessServices]).map((service) => (
                <Chip
                  key={service}
                  label={service}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        </>
      )}

      {/* Company Details */}
      {(business.gstNumber || business.panNumber || business.companyStartDate || business.numberOfEmployees || business.googleMyBusinessLink) && (
        <>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2.5, pl: 1.5, borderLeft: '4px solid', borderColor: 'primary.main', lineHeight: 1.2 }}>
            Company Details
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {business.gstNumber && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              p: 2.5,
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%',
              transition: 'border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: theme.shadows[3],
                transform: 'translateY(-2px)',
              },
            }}
          >
                  <Badge sx={{ fontSize: 20, color: 'primary.main', mt: 0.5 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.68rem', fontWeight: 600, mb: 0.4 }}>
                      GST Number
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {business.gstNumber}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
            {business.panNumber && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              p: 2.5,
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%',
              transition: 'border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: theme.shadows[3],
                transform: 'translateY(-2px)',
              },
            }}
          >
                  <Badge sx={{ fontSize: 20, color: 'primary.main', mt: 0.5 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.68rem', fontWeight: 600, mb: 0.4 }}>
                      PAN Number
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {business.panNumber}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
            {business.companyStartDate && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              p: 2.5,
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%',
              transition: 'border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: theme.shadows[3],
                transform: 'translateY(-2px)',
              },
            }}
          >
                  <CalendarMonth sx={{ fontSize: 20, color: 'primary.main', mt: 0.5 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.68rem', fontWeight: 600, mb: 0.4 }}>
                      Company Start Date
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {moment(business.companyStartDate).format('DD MMM YYYY')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
            {business.numberOfEmployees && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              p: 2.5,
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%',
              transition: 'border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: theme.shadows[3],
                transform: 'translateY(-2px)',
              },
            }}
          >
                  <People sx={{ fontSize: 20, color: 'primary.main', mt: 0.5 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.68rem', fontWeight: 600, mb: 0.4 }}>
                      Number of Employees
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {business.numberOfEmployees}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
            {business.googleMyBusinessLink && (
              <Grid size={{ xs: 12 }}>
                <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              p: 2.5,
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%',
              transition: 'border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: theme.shadows[3],
                transform: 'translateY(-2px)',
              },
            }}
          >
                  <Map sx={{ fontSize: 20, color: 'primary.main', mt: 0.5 }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.68rem', fontWeight: 600, mb: 0.4 }}>
                      Google My Business
                    </Typography>
                    <Link
                      href={business.googleMyBusinessLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      sx={{ wordBreak: 'break-all' }}
                    >
                      View on Google Maps
                    </Link>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>

          {/* Google Maps Embed - only show if link contains /embed */}
          {business.googleMyBusinessLink && business.googleMyBusinessLink.includes('/embed') && (
            <Card sx={{ borderRadius: 3, overflow: 'hidden', mt: 2 }}>
              <Box
                component="iframe"
                src={business.googleMyBusinessLink}
                sx={{
                  width: '100%',
                  height: { xs: 250, sm: 300, md: 350 },
                  border: 0,
                }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Card>
          )}
        </>
      )}

      {/* Head Office Address */}
      {business.headOfficeAddress && (business.headOfficeAddress.addressLine1 || business.headOfficeAddress.city) && (
        <>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2.5, pl: 1.5, borderLeft: '4px solid', borderColor: 'primary.main', lineHeight: 1.2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Apartment /> Head Office Address
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              p: 2.5,
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              mb: 3,
              transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
              '&:hover': {
                borderColor: 'secondary.main',
                boxShadow: theme.shadows[3],
              },
            }}
          >
            <LocationOn sx={{ fontSize: 22, color: 'secondary.main', mt: 0.2 }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                {business.headOfficeAddress.addressLine1}
                {business.headOfficeAddress.addressLine2 && `, ${business.headOfficeAddress.addressLine2}`}
                <br />
                {business.headOfficeAddress.landmark && `${business.headOfficeAddress.landmark}, `}
                {business.headOfficeAddress.city && `${business.headOfficeAddress.city}, `}
                {business.headOfficeAddress.state && `${business.headOfficeAddress.state} `}
                {business.headOfficeAddress.pincode && `- ${business.headOfficeAddress.pincode}`}
              </Typography>
            </Box>
          </Box>
        </>
      )}

      {/* Business Hours */}
      {business.businessHours && business.businessHours.length > 0 && (
        <>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2.5, pl: 1.5, borderLeft: '4px solid', borderColor: 'primary.main', lineHeight: 1.2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Schedule /> Business Hours
          </Typography>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
            <CardContent>
              {DAYS_OF_WEEK.map((day) => {
                const dayHours = business.businessHours?.find((h) => h.day === day);
                return (
                  <Box
                    key={day}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      py: 1,
                      borderBottom: day !== 'Sunday' ? `1px solid ${theme.palette.divider}` : 'none',
                    }}
                  >
                    <Typography variant="body2" fontWeight={500}>
                      {day}
                    </Typography>
                    <Typography variant="body2" color={dayHours?.isClosed ? 'error' : 'text.secondary'}>
                      {dayHours?.isClosed
                        ? 'Closed'
                        : dayHours?.openTime && dayHours?.closeTime
                        ? `${formatTime(dayHours.openTime)} - ${formatTime(dayHours.closeTime)}`
                        : 'Not specified'}
                    </Typography>
                  </Box>
                );
              })}
            </CardContent>
          </Card>
        </>
      )}

      {/* Social Media */}
      {business.socialMedia &&
        (business.socialMedia.instagram ||
          business.socialMedia.facebook ||
          business.socialMedia.youtube ||
          business.socialMedia.twitter) && (
          <>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2.5, pl: 1.5, borderLeft: '4px solid', borderColor: 'primary.main', lineHeight: 1.2 }}>
              Social Media
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
              {business.socialMedia.instagram && (
                <Button
                  component={Link}
                  href={business.socialMedia.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  startIcon={<Instagram />}
                  sx={{ textTransform: 'none' }}
                >
                  Instagram
                </Button>
              )}
              {business.socialMedia.facebook && (
                <Button
                  component={Link}
                  href={business.socialMedia.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  startIcon={<Facebook />}
                  sx={{ textTransform: 'none' }}
                >
                  Facebook
                </Button>
              )}
              {business.socialMedia.youtube && (
                <Button
                  component={Link}
                  href={business.socialMedia.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  startIcon={<YouTube />}
                  sx={{ textTransform: 'none' }}
                >
                  YouTube
                </Button>
              )}
              {business.socialMedia.twitter && (
                <Button
                  component={Link}
                  href={business.socialMedia.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  startIcon={<Twitter />}
                  sx={{ textTransform: 'none' }}
                >
                  Twitter/X
                </Button>
              )}
            </Box>
          </>
        )}

      {/* Business Documents */}
      {business.documents && business.documents.length > 0 && (
        <>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2.5, pl: 1.5, borderLeft: '4px solid', borderColor: 'primary.main', lineHeight: 1.2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachFile /> Business Documents
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {business.documents.map((doc, index) => (
              <Card key={index} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {doc.documentType}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getFileName(doc.fileId)}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PictureAsPdf />}
                    onClick={() =>
                      handleOpenPdfPreview(
                        getFileUrl(doc.fileId),
                        `${doc.documentType} Document`
                      )
                    }
                    sx={{ textTransform: 'none' }}
                  >
                    View
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Box>
        </>
      )}

      {/* Owner ID Proof */}
      {business.ownerIdProofId && getFileUrl(business.ownerIdProofId) && (
        <>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2.5, pl: 1.5, borderLeft: '4px solid', borderColor: 'primary.main', lineHeight: 1.2 }}>
            Owner ID Proof
          </Typography>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
            <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2">Owner Identification Document</Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<PictureAsPdf />}
                onClick={() =>
                  handleOpenPdfPreview(
                    getFileUrl(business.ownerIdProofId),
                    'Owner ID Proof'
                  )
                }
                sx={{ textTransform: 'none' }}
              >
                View
              </Button>
            </CardContent>
          </Card>
        </>
      )}

      {/* Rejection/Clarification Notes */}
      {business.rejectionReason && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'error.lighter', borderRadius: 2, border: `1px solid ${theme.palette.error.main}` }}>
          <Typography variant="subtitle2" fontWeight={600} color="error" gutterBottom>
            Rejection Reason
          </Typography>
          <Typography variant="body2" color="error.dark">
            {business.rejectionReason}
          </Typography>
        </Box>
      )}

      {business.clarificationNote && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.lighter', borderRadius: 2, border: `1px solid ${theme.palette.warning.main}` }}>
          <Typography variant="subtitle2" fontWeight={600} color="warning.dark" gutterBottom>
            Clarification Required
          </Typography>
          <Typography variant="body2" color="warning.dark">
            {business.clarificationNote}
          </Typography>
        </Box>
      )}
        </>
      )}

      {/* Products/Services Tab - Only for verified businesses */}
      {activeTab === 1 && business.isVerified && (
        <Box>
          {/* Filter Toggle */}
          <Box sx={{ mb: 3 }}>
            <ToggleButtonGroup
              value={serviceTypeFilter}
              exclusive
              onChange={(_, value) => value && setServiceTypeFilter(value)}
              size="small"
            >
              <ToggleButton value="all" sx={{ textTransform: 'none', px: 2 }}>
                All
              </ToggleButton>
              <ToggleButton value="product" sx={{ textTransform: 'none', px: 2 }}>
                <Inventory sx={{ mr: 0.5, fontSize: 18 }} /> Products
              </ToggleButton>
              <ToggleButton value="service" sx={{ textTransform: 'none', px: 2 }}>
                <MiscellaneousServices sx={{ mr: 0.5, fontSize: 18 }} /> Services
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {isLoadingProducts ? (
            <Grid container spacing={2}>
              {[1, 2, 3].map((i) => (
                <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
                </Grid>
              ))}
            </Grid>
          ) : serviceProducts.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                border: `1px dashed ${theme.palette.divider}`,
                borderRadius: 2,
                bgcolor: 'background.paper',
              }}
            >
              <Inventory sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No products or services listed yet
              </Typography>
              <Typography variant="body2" color="text.disabled">
                This business has not added any products or services yet
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {serviceProducts.map((product) => (
                <Grid key={product._id} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card
                    elevation={0}
                    onClick={() => {
                      const bizId = typeof product.businessId === 'string'
                        ? product.businessId
                        : product.businessId?._id || id;
                      navigate(`/businesses/${bizId}/serviceProducts/${product._id}`);
                    }}
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: 2,
                      },
                    }}
                  >
                    {/* Promoted Badge */}
                    {product.isPromoted && (
                      <Chip
                        icon={<Star sx={{ fontSize: 14 }} />}
                        label="Promoted"
                        size="small"
                        color="warning"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          zIndex: 1,
                          height: 22,
                          fontSize: '0.7rem',
                        }}
                      />
                    )}

                    {/* Product Image */}
                    {product.productImage?.url ? (
                      <CardMedia
                        component="img"
                        height={180}
                        image={product.productImage.url}
                        alt={product.title}
                        sx={{ objectFit: 'cover' }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 180,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                        }}
                      >
                        {product.serviceType === 'product' ? (
                          <Inventory sx={{ fontSize: 56, color: 'text.disabled' }} />
                        ) : (
                          <MiscellaneousServices sx={{ fontSize: 56, color: 'text.disabled' }} />
                        )}
                      </Box>
                      
                    )}

                    <CardContent sx={{ flexGrow: 1 }}>
                      {/* Type Chip */}
                      <Chip
                        label={product.serviceType === 'product' ? 'Product' : 'Service'}
                        size="small"
                        color={product.serviceType === 'product' ? 'primary' : 'secondary'}
                        variant="outlined"
                        sx={{ mb: 1, height: 22, fontSize: '0.7rem' }}
                      />

                      {/* Title */}
                      <Typography variant="subtitle1" fontWeight={600} noWrap>
                        {product.title}
                      </Typography>

                      {/* Price */}
                      <Typography variant="h6" color="primary" fontWeight={700} sx={{ my: 0.5 }}>
                        {product.price !== undefined && product.price > 0
                          ? `₹${product.price.toLocaleString('en-IN')}`
                          : 'Price on Request'}
                      </Typography>

                      {/* Description */}
                      {product.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            mt: 0.5,
                          }}
                        >
                          {product.description}
                        </Typography>
                      )}

                      {/* Action Buttons */}
                      <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                        {/* Brochure Link */}
                        {product.brochure?.url && (
                          <Button
                            component={Link}
                            href={product.brochure.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            startIcon={<PictureAsPdf />}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            sx={{ textTransform: 'none' }}
                          >
                            Brochure
                          </Button>
                        )}

                        {/* Share Button */}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShareProduct(product);
                          }}
                          sx={{ border: '1px solid', borderColor: 'divider' }}
                        >
                          <ShareOutlined fontSize="small" />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* Courses Tab - Only for verified businesses */}
      {activeTab === 2 && business.isVerified && (
        <Box>
          {/* Filters */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            <ToggleButtonGroup
              value={courseLevelFilter}
              exclusive
              onChange={(_, value) => value && setCourseLevelFilter(value)}
              size="small"
            >
              <ToggleButton value="all" sx={{ textTransform: 'none', px: 2 }}>All Levels</ToggleButton>
              <ToggleButton value="Beginner" sx={{ textTransform: 'none', px: 2 }}>Beginner</ToggleButton>
              <ToggleButton value="Intermediate" sx={{ textTransform: 'none', px: 2 }}>Intermediate</ToggleButton>
              <ToggleButton value="Advanced" sx={{ textTransform: 'none', px: 2 }}>Advanced</ToggleButton>
            </ToggleButtonGroup>

            <ToggleButtonGroup
              value={courseTypeFilter}
              exclusive
              onChange={(_, value) => value && setCourseTypeFilter(value)}
              size="small"
            >
              <ToggleButton value="all" sx={{ textTransform: 'none', px: 2 }}>All</ToggleButton>
              <ToggleButton value="Free" sx={{ textTransform: 'none', px: 2 }}>Free</ToggleButton>
              <ToggleButton value="Paid" sx={{ textTransform: 'none', px: 2 }}>Paid</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {isLoadingCourses ? (
            <Grid container spacing={2}>
              {[1, 2, 3].map((i) => (
                <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
                </Grid>
              ))}
            </Grid>
          ) : courses.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                border: `1px dashed ${theme.palette.divider}`,
                borderRadius: 2,
                bgcolor: 'background.paper',
              }}
            >
              <School sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No courses listed yet
              </Typography>
              <Typography variant="body2" color="text.disabled">
                This business has not added any courses yet
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {courses.map((course) => (
                <Grid key={course._id} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card
                    elevation={0}
                    onClick={() => {
                      const bizId = typeof course.businessId === 'string'
                        ? course.businessId
                        : course.businessId?._id || id;
                      navigate(`/businesses/${bizId}/courses/${course._id}`);
                    }}
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: 2,
                      },
                    }}
                  >
                    {(course.isCurrentlyPromoted || course.isPromoted) && (
                      <Chip
                        icon={<Star sx={{ fontSize: 14 }} />}
                        label="Promoted"
                        size="small"
                        color="warning"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          zIndex: 1,
                          height: 22,
                          fontSize: '0.7rem',
                        }}
                      />
                    )}

                    {course.courseThumbnail?.url ? (
                      <CardMedia
                        component="img"
                        height={180}
                        image={course.courseThumbnail.url}
                        alt={course.courseTitle}
                        sx={{ objectFit: 'cover' }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 180,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                        }}
                      >
                        <School sx={{ fontSize: 56, color: 'text.disabled' }} />
                      </Box>
                    )}

                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={course.level}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                        <Chip
                          label={course.courseType}
                          size="small"
                          color={course.courseType === 'Paid' ? 'secondary' : 'success'}
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                        {course.certificationAvailable && (
                          <Chip
                            icon={<WorkspacePremium sx={{ fontSize: 12 }} />}
                            label="Certified"
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ height: 22, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>

                      <Typography variant="subtitle1" fontWeight={600} noWrap>
                        {course.courseTitle}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, my: 0.5 }}>
                        {course.courseType === 'Free' ? (
                          <Typography variant="h6" color="success.main" fontWeight={700}>
                            Free
                          </Typography>
                        ) : (
                          <>
                            <Typography variant="h6" color="primary" fontWeight={700}>
                              {course.finalPrice !== undefined && course.finalPrice >= 0
                                ? `₹${course.finalPrice.toLocaleString('en-IN')}`
                                : course.price
                                ? `₹${course.price.toLocaleString('en-IN')}`
                                : 'Price on Request'}
                            </Typography>
                            {course.discount && course.discount > 0 && course.price ? (
                              <>
                                <Typography
                                  variant="body2"
                                  color="text.disabled"
                                  sx={{ textDecoration: 'line-through' }}
                                >
                                  ₹{course.price.toLocaleString('en-IN')}
                                </Typography>
                                <Typography variant="caption" color="error" fontWeight={600}>
                                  {course.discount}% off
                                </Typography>
                              </>
                            ) : null}
                          </>
                        )}
                      </Box>

                      {(course.duration || course.numberOfModules) && (
                        <Box sx={{ display: 'flex', gap: 1.5, color: 'text.secondary', mb: 0.5 }}>
                          {course.duration && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <AccessTime sx={{ fontSize: 14 }} />
                              <Typography variant="caption">{course.duration}</Typography>
                            </Box>
                          )}
                          {course.numberOfModules ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <School sx={{ fontSize: 14 }} />
                              <Typography variant="caption">{course.numberOfModules} modules</Typography>
                            </Box>
                          ) : null}
                        </Box>
                      )}

                      {course.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                          sx={{ mt: 0.5 }}
                        >
                          {slateJsonToText(course.description)}
                        </Typography>
                      )}

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                          mt: 'auto',
                          pt: 1.5,
                        }}
                      >
                        {course.brochure?.url ? (
                          <Button
                            component={Link}
                            href={course.brochure.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            startIcon={<PictureAsPdf />}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            sx={{ textTransform: 'none' }}
                          >
                            Brochure
                          </Button>
                        ) : (
                          <Box />
                        )}

                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShareCourse(course);
                          }}
                          sx={{ border: '1px solid', borderColor: 'divider' }}
                        >
                          <ShareOutlined fontSize="small" />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* Admin Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {business.approvalStatus === 'pending' || business.approvalStatus === 'in_review' || business.approvalStatus === 'clarification_needed' ? (
          [
            <MuiMenuItem key="approve" onClick={() => handleMenuAction(handleApprove)}>
              <CheckCircle fontSize="small" sx={{ mr: 1 }} color="success" />
              Approve Business
            </MuiMenuItem>,
            <MuiMenuItem key="reject" onClick={() => handleMenuAction(handleReject)}>
              <Cancel fontSize="small" sx={{ mr: 1 }} color="error" />
              Reject Business
            </MuiMenuItem>,
            <MuiMenuItem key="inreview" onClick={() => handleMenuAction(handleSetInReview)}>
              <Info fontSize="small" sx={{ mr: 1 }} color="info" />
              Set In Review
            </MuiMenuItem>,
            <MuiMenuItem key="clarification" onClick={() => handleMenuAction(handleRequestClarification)}>
              <HelpOutline fontSize="small" sx={{ mr: 1 }} color="warning" />
              Request Clarification
            </MuiMenuItem>,
          ]
        ) : null}

        {business.approvalStatus === 'approved' && [
          business.isSuspended ? (
            <MuiMenuItem key="activate" onClick={() => handleMenuAction(handleActivate)}>
              <PlayArrow fontSize="small" sx={{ mr: 1 }} color="success" />
              Activate Business
            </MuiMenuItem>
          ) : (
            <MuiMenuItem key="suspend" onClick={() => handleMenuAction(handleSuspend)}>
              <Block fontSize="small" sx={{ mr: 1 }} color="warning" />
              Suspend Business
            </MuiMenuItem>
          ),
          business.isBlocked ? (
            <MuiMenuItem key="unblock" onClick={() => handleMenuAction(handleUnblock)}>
              <CheckCircle fontSize="small" sx={{ mr: 1 }} color="success" />
              Unblock Business
            </MuiMenuItem>
          ) : (
            <MuiMenuItem key="block" onClick={() => handleMenuAction(handleBlock)}>
              <Block fontSize="small" sx={{ mr: 1 }} color="error" />
              Block Business
            </MuiMenuItem>
          ),
        ]}
      </Menu>

      {/* PDF Preview Dialog */}
      <Dialog
        open={pdfPreviewOpen}
        onClose={handleClosePdfPreview}
        maxWidth="lg"
        fullWidth
        slotProps={{
          paper: { sx: { height: '90vh' } },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{pdfFileName}</Typography>
          <IconButton onClick={handleClosePdfPreview}>
            <ArrowBack />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {pdfUrl && <CanvasPdfViewer url={pdfUrl} fileName={pdfFileName} />}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Business</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Please provide a reason for rejection..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRejectSubmit} variant="contained" color="error">
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clarification Dialog */}
      <Dialog open={clarificationDialogOpen} onClose={() => setClarificationDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Clarification</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Clarification Note"
            value={clarificationNote}
            onChange={(e) => setClarificationNote(e.target.value)}
            placeholder="Please provide details about what clarification is needed..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClarificationDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleClarificationSubmit} variant="contained" color="warning">
            Request Clarification
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialogOpen}
        title={confirmAction?.title || ''}
        message={confirmAction?.message || ''}
        onConfirm={executeConfirmAction}
        onCancel={() => {
          setConfirmDialogOpen(false);
          setConfirmAction(null);
        }}
        severity={confirmAction?.action === 'block' || confirmAction?.action === 'suspend' ? 'error' : 'info'}
      />

      {/* Share Product Modal */}
      <ShareProductModal
        open={!!shareProduct}
        onClose={() => setShareProduct(null)}
        product={shareProduct}
        businessId={id!}
      />

      {/* Share Course Modal */}
      <ShareCourseModal
        open={!!shareCourse}
        onClose={() => setShareCourse(null)}
        course={shareCourse}
        businessId={id!}
      />
    </Box>
  );
};
